import {
  safeParseJson
} from "./chunk-3L7ZBNA4.mjs";
import {
  createCookie,
  readCookie
} from "./chunk-LLC745D6.mjs";

// src/colors/index.ts
var COLORS = {};
var randomDarkColor = () => {
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
};
var randomColorByUserId = (id) => {
  if (COLORS[id]) {
    return COLORS[id];
  }
  const savedColor = readCookie("color");
  const parsedColor = safeParseJson(savedColor || "");
  if (parsedColor) {
    const saved = parsedColor[id];
    if (saved) {
      COLORS[id] = saved;
      return saved;
    }
  }
  const bg = randomDarkColor();
  COLORS[id] = bg;
  createCookie({
    name: "color",
    value: JSON.stringify({ [id]: bg })
  });
  return bg;
};
var hexToHSL = (hex) => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt("0x" + hex[1] + hex[1]);
    g = parseInt("0x" + hex[2] + hex[2]);
    b = parseInt("0x" + hex[3] + hex[3]);
  } else if (hex.length === 7) {
    r = parseInt("0x" + hex[1] + hex[2]);
    g = parseInt("0x" + hex[3] + hex[4]);
    b = parseInt("0x" + hex[5] + hex[6]);
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b), cmax = Math.max(r, g, b), delta = cmax - cmin;
  let h = 0, s = 0, l = 0;
  if (delta === 0) h = 0;
  else if (cmax === r) h = (g - b) / delta % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  h = Math.round(h * 60);
  if (h < 0) h += 360;
  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);
  return { h, s, l };
};
var formatHSL = (h, s, l) => {
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
};
var getColorLuminance = (hex) => {
  const { l } = hexToHSL(hex);
  return l > 50 ? "light" : "dark";
};
var generateColorShades = (hex) => {
  const { h, s } = hexToHSL(hex);
  const baseL = hexToHSL(hex).l;
  const lightnessValues = {
    100: Math.min(baseL + 19, 94),
    200: Math.min(baseL + 14, 89),
    300: Math.min(baseL + 9, 83),
    400: Math.min(baseL + 4, 77),
    500: baseL,
    600: Math.max(baseL - 6, 8),
    700: Math.max(baseL - 11, 8),
    800: Math.max(baseL - 16, 8),
    900: Math.max(baseL - 21, 8)
  };
  const saturations = {
    100: s,
    200: s,
    300: s,
    400: s,
    500: s,
    600: Math.max(s - 8, 0),
    700: Math.max(s - 8, 0),
    800: Math.max(s - 8, 0),
    900: Math.max(s - 8, 0)
  };
  return {
    100: formatHSL(h, saturations[100], lightnessValues[100]),
    200: formatHSL(h, saturations[200], lightnessValues[200]),
    300: formatHSL(h, saturations[300], lightnessValues[300]),
    400: formatHSL(h, saturations[400], lightnessValues[400]),
    500: formatHSL(h, saturations[500], lightnessValues[500]),
    600: formatHSL(h, saturations[600], lightnessValues[600]),
    700: formatHSL(h, saturations[700], lightnessValues[700]),
    800: formatHSL(h, saturations[800], lightnessValues[800]),
    900: formatHSL(h, saturations[900], lightnessValues[900])
  };
};
var getContrastColor = (hex) => {
  const luminance = getColorLuminance(hex);
  return luminance === "light" ? "0 0% 0%" : "0 0% 100%";
};
var generateThemeColorVars = (colorName, hex, contrastHex) => {
  const shades = generateColorShades(hex);
  let contrast = "";
  if (contrastHex) {
    const c = hexToHSL(contrastHex);
    contrast = formatHSL(c.h, c.s, c.l);
  } else {
    contrast = getContrastColor(hex);
  }
  return {
    [`--color-${colorName}-100`]: shades[100],
    [`--color-${colorName}-200`]: shades[200],
    [`--color-${colorName}-300`]: shades[300],
    [`--color-${colorName}-400`]: shades[400],
    [`--color-${colorName}-500`]: shades[500],
    [`--color-${colorName}-600`]: shades[600],
    [`--color-${colorName}-700`]: shades[700],
    [`--color-${colorName}-800`]: shades[800],
    [`--color-${colorName}-900`]: shades[900],
    [`--color-${colorName}-contrast`]: contrast
  };
};
var generateTextColorVars = (hex) => {
  const { h, s, l } = hexToHSL(hex);
  const isLightText = l > 50;
  const adjustedSaturation = Math.max(s - 1, 0);
  const adjustLightness = (delta) => isLightText ? Math.max(l - delta, 20) : Math.min(l + delta, 100);
  return {
    ["--color-text-primary"]: formatHSL(h, s, l),
    ["--color-text-secondary"]: formatHSL(h, adjustedSaturation, adjustLightness(24)),
    ["--color-text-tertiary"]: formatHSL(h, adjustedSaturation, adjustLightness(55)),
    ["--color-text-disabled"]: "0 0% 30%",
    ["--color-text-link"]: "var(--color-primary-500)"
  };
};
var generateBodyColorVars = (hex) => {
  const { h, s, l } = hexToHSL(hex);
  const isLight = l > 50;
  const levels = isLight ? {
    body: formatHSL(h, s, l),
    level1: formatHSL(h, s, Math.max(l - 11, 0)),
    level2: formatHSL(h, s, Math.max(l - 22, 0)),
    level3: formatHSL(h, s, Math.max(l - 33, 0)),
    level4: formatHSL(h, s, Math.max(l - 44, 0)),
    contrast: "0 0% 0%"
  } : {
    body: formatHSL(h, s, l),
    level1: formatHSL(h, s, Math.min(l + 11, 100)),
    level2: formatHSL(h, s, Math.min(l + 22, 100)),
    level3: formatHSL(h, s, Math.min(l + 33, 100)),
    level4: formatHSL(h, s, Math.min(l + 44, 100)),
    contrast: "0 0% 100%"
  };
  return {
    ["--bg-body"]: levels.body,
    ["--bg-body-level-1"]: levels.level1,
    ["--bg-body-level-2"]: levels.level2,
    ["--bg-body-level-3"]: levels.level3,
    ["--bg-body-level-4"]: levels.level4,
    ["--bg-body-contrast"]: levels.contrast
  };
};
var generateInputVars = (bodyHex) => {
  if (!bodyHex) {
    return "";
  }
  const { h, s, l } = hexToHSL(bodyHex);
  const isLight = l > 50;
  if (isLight) {
    return {
      ["--input-bg"]: formatHSL(h, s, l),
      ["--input-bg-hover"]: formatHSL(h, s, Math.max(l - 5, 0)),
      ["--input-bg-disabled"]: formatHSL(h, s, Math.max(l - 20, 0)),
      ["--input-border-primary"]: "var(--bg-body-level-1)",
      ["--input-border"]: "var(--bg-body-level-1)",
      ["--input-border-focus"]: "0 0% 0%",
      ["--input-popover-bg"]: formatHSL(h, s, Math.max(l - 5, 0))
    };
  } else {
    return {
      ["--input-bg"]: formatHSL(h, s, l),
      ["--input-bg-hover"]: formatHSL(h, s, Math.min(l + 5, 100)),
      ["--input-bg-disabled"]: formatHSL(h, s, Math.min(l + 20, 100)),
      ["--input-border-primary"]: "var(--bg-body-level-1)",
      ["--input-border"]: "var(--bg-body-level-1)",
      ["--input-border-focus"]: "0 0% 100%",
      ["--input-popover-bg"]: formatHSL(h, s, Math.min(l + 5, 100))
    };
  }
};
var getFlowCheckoutAppearance = () => {
  const css = getComputedStyle(document.documentElement);
  const hsl = (v, alpha = 1) => {
    const raw = css.getPropertyValue(v).trim();
    return raw ? `hsl(${raw} / ${alpha})` : void 0;
  };
  const rawVar = (v) => css.getPropertyValue(v).trim() || void 0;
  const radius = rawVar("--radius-S") ?? "8px";
  return {
    colorAction: hsl("--color-action-500"),
    // CTA button fill
    colorBackground: hsl("--bg-body"),
    // widget background
    colorBorder: hsl("--bg-body-level-1"),
    // divider / outer border
    colorDisabled: hsl("--color-text-disabled"),
    // disabled inputs & text
    colorError: hsl("--color-error-500"),
    // error messages / outlines
    colorFormBackground: hsl("--input-bg"),
    // card-number field bg
    colorFormBorder: hsl("--bg-body-level-1"),
    // card-number field border
    colorInverse: hsl("--color-action-contrast"),
    // text on CTA button
    colorOutline: hsl("--input-border-focus", 0.4),
    focusOutlineWidth: "2px",
    colorPrimary: hsl("--color-text-primary"),
    // heading / label text
    colorSecondary: hsl("--color-text-secondary"),
    // helper / secondary text
    colorSuccess: hsl("--color-success-500"),
    // success state
    borderRadius: [radius, radius]
  };
};
var generateThemeVars = (colors) => {
  return {
    ...colors.primary && generateThemeColorVars("primary", colors.primary, colors.primaryContrast),
    ...colors.secondary && generateThemeColorVars("secondary", colors.secondary),
    ...colors.tertiary && generateThemeColorVars("tertiary", colors.tertiary),
    ...colors.action && generateThemeColorVars("action", colors.action),
    ...colors.textPrimary && generateTextColorVars(colors.textPrimary),
    ...colors.body && generateBodyColorVars(colors.body),
    ...colors.body && generateInputVars(colors.body)
  };
};

export {
  randomDarkColor,
  randomColorByUserId,
  hexToHSL,
  formatHSL,
  getColorLuminance,
  generateColorShades,
  getContrastColor,
  generateThemeColorVars,
  generateTextColorVars,
  generateBodyColorVars,
  generateInputVars,
  getFlowCheckoutAppearance,
  generateThemeVars
};
//# sourceMappingURL=chunk-NREXCY5Q.mjs.map